import React, { lazy, Suspense, useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useSession } from "./lib/session.tsx";
import { PREVIEWABLE_ROLES } from "./lib/roles.ts";
import type { StringKey } from "./lib/i18n.ts";
import { Assistant } from "./components/Assistant.tsx";
import { Wizard, wizardDone } from "./components/Wizard.tsx";
import { Search } from "./components/Search.tsx";
import { DemoExperience } from "./components/DemoTour.tsx";
import { Onboarding } from "./components/Onboarding.tsx";
import { PasswordChange } from "./components/PasswordChange.tsx";
import Login from "./pages/Login.tsx";
import Landing from "./pages/Landing.tsx";
const Dashboard = lazy(() => import("./pages/Dashboard.tsx"));
const Advisor = lazy(() => import("./pages/Advisor.tsx"));
const Memory = lazy(() => import("./pages/Memory.tsx"));
const WarRoom = lazy(() => import("./pages/WarRoom.tsx"));
const Watchdog = lazy(() => import("./pages/Watchdog.tsx"));
const Field = lazy(() => import("./pages/Field.tsx"));
const Calendar = lazy(() => import("./pages/Calendar.tsx"));
const Board = lazy(() => import("./pages/Board.tsx"));
const Projects = lazy(() => import("./pages/Projects.tsx"));
const Settings = lazy(() => import("./pages/Settings.tsx"));
const Guide = lazy(() => import("./pages/Guide.tsx"));
const Console = lazy(() => import("./pages/Console.tsx"));
const Evidence = lazy(() => import("./pages/Evidence.tsx"));
const EvidenceDetail = lazy(() => import("./pages/EvidenceDetail.tsx"));
const Work = lazy(() => import("./pages/Work.tsx"));
const Finance = lazy(() => import("./pages/Finance.tsx"));
const Admin = lazy(() => import("./pages/Admin.tsx"));
const Filings = lazy(() => import("./pages/Filings.tsx"));
const Contacts = lazy(() => import("./pages/Contacts.tsx"));
const Issues = lazy(() => import("./pages/Issues.tsx"));
const Decisions = lazy(() => import("./pages/Decisions.tsx"));
const Risks = lazy(() => import("./pages/Risks.tsx"));
const Vendors = lazy(() => import("./pages/Vendors.tsx"));
const ClaimsReview = lazy(() => import("./pages/ClaimsReview.tsx"));
const Report = lazy(() => import("./pages/Report.tsx"));
const Hermes = lazy(() => import("./pages/Hermes.tsx"));
const Billing = lazy(() => import("./pages/Billing.tsx"));
const Strategy = lazy(() => import("./pages/Strategy.tsx"));
const Briefing = lazy(() => import("./pages/Briefing.tsx"));
const CallTime = lazy(() => import("./pages/CallTime.tsx"));
const Practice = lazy(() => import("./pages/Practice.tsx"));
const MediaScout = lazy(() => import("./pages/MediaScout.tsx"));

/**
 * Page-level permission gates — one table drives the route guard. A route
 * absent here is open to every active user (data is still filtered by
 * Security Rules). Any listed permission admits.
 */
const ROUTE_GATES = new Map<string, readonly string[]>([
  ["/today", ["briefing.view"]],
  // Evidence, the team chat and the live staff-location map were reachable by
  // anyone signed in; only Security Rules stopped the data. Gate the pages too.
  ["/evidence", ["evidence.ingest", "aura.query"]],
  ["/board", ["projects.view"]],
  ["/field", ["field.manage"]],
  ["/call-time", ["calltime.use"]],
  ["/practice", ["rapidresponse.practice"]],
  ["/media-scout", ["media.scout"]],
  ["/strategy", ["strategy.view"]],
  ["/advisor", ["aura.query"]],
  ["/memory", ["aura.query"]],
  ["/war-room", ["aura.query"]],
  ["/watchdog", ["aura.query"]],
  ["/claims-review", ["aura.canonical.approve"]],
  ["/contacts", ["crm.view"]],
  ["/work", ["projects.view"]],
  ["/projects", ["projects.view"]],
  ["/issues", ["projects.view"]],
  ["/decisions", ["projects.view"]],
  ["/risks", ["projects.view"]],
  ["/calendar", ["projects.view"]],
  ["/report", ["projects.view"]],
  ["/hermes", ["hermes.run"]],
  ["/finance", ["finance.view"]],
  ["/vendors", ["finance.view"]],
  ["/filings", ["finance.view"]],
  ["/admin", ["admin.users.manage", "audit.view"]],
  ["/console", ["system.maintenance"]],
]);

function Gate({ path, children }: { path: string; children: React.ReactNode }) {
  const { can, permissions, t } = useSession();
  const needed = ROUTE_GATES.get(path);
  const admitted =
    !needed || needed.some((p) => (p === "system.maintenance" ? permissions.includes(p) : can(p)));
  if (!admitted) return <div className="callout callout-warn">{t("common.noPermission")}</div>;
  return <>{children}</>;
}

/**
 * Whether `/` is the candidate's phone-first briefing rather than the Command
 * Center: the effective role is candidate (the "View as" role when previewing,
 * otherwise the real roles) and briefing.view is held.
 */
function useCandidateHome(): boolean {
  const { can, viewAsRole, profile } = useSession();
  const candidate = viewAsRole
    ? viewAsRole === "candidate"
    : (profile?.roles?.includes("candidate") ?? false);
  return candidate && can("briefing.view");
}

/** `/`: the briefing for the candidate, the Command Center for everyone else. */
function Home() {
  return useCandidateHome() ? <Briefing /> : <Dashboard />;
}

/**
 * `locked`: the campaign has no license, trial or bypass. The shell stays (so
 * nobody is thrown out to a bare paywall) but only Billing and the Guide are
 * reachable, since every data read would be refused by the rules anyway.
 */
function Sidebar({
  onOpenSearch,
  locked = false,
  open = false,
  onClose,
}: {
  onOpenSearch: () => void;
  locked?: boolean;
  /** Below 820px the rail is an off-canvas drawer; above it this is ignored. */
  open?: boolean;
  onClose?: () => void;
}) {
  const {
    t,
    profile,
    permissions,
    realPermissions,
    viewAsRole,
    setViewAsRole,
    locale,
    setLocale,
    logout,
    can,
    trialActive,
    licensed,
    developerBypass,
  } = useSession();
  const canPreview = realPermissions.includes("*");
  const candidateHome = useCandidateHome();

  return (
    <aside
      className="sidebar"
      data-open={open ? "true" : "false"}
      onClick={(e) => {
        // Any nav link inside the drawer closes it.
        // SAFETY: a React click event inside this <aside> always originates
        // from an element in this document, so the target is an HTMLElement.
        const target = e.target as HTMLElement;
        if (onClose && target.closest("a")) onClose();
      }}
    >
      <div>
        <div className="brand-name">{t("app.name")}</div>
        <div className="brand-tagline">{t("app.tagline")}</div>
      </div>

      {!licensed && !developerBypass && (
        <NavLink
          to="/billing"
          className="nav-activate"
          style={{
            display: "block",
            padding: "0.6rem 0.75rem",
            borderRadius: 8,
            border: "1px solid #d6b25e",
            background: "rgba(214, 178, 94, 0.14)",
            color: "#f1d48a",
            fontWeight: 600,
            textDecoration: "none",
          }}
        >
          ★ {t("nav.activate")}
        </NavLink>
      )}

      {!locked && (
        <button
          type="button"
          className="subtle"
          onClick={onOpenSearch}
          style={{ textAlign: "left", width: "100%" }}
        >
          🔍 {t("search.open")} <span className="faint small mono">Ctrl+K</span>
        </button>
      )}

      <nav className="nav">
        {locked ? (
          <>
            <NavLink to="/billing">{t("nav.billing")}</NavLink>
            <NavLink to="/guide">{t("nav.guide")}</NavLink>
          </>
        ) : (
          <>
            {can("briefing.view") && <NavLink to="/today">{t("nav.today")}</NavLink>}
            {/* When `/` is the candidate's briefing, the Command Center moves to its own path. */}
            <NavLink to={candidateHome ? "/command-center" : "/"} end>
              {t("nav.dashboard")}
            </NavLink>
            {can("calltime.use") && <NavLink to="/call-time">{t("nav.callTime")}</NavLink>}
            {can("rapidresponse.practice") && <NavLink to="/practice">{t("nav.practice")}</NavLink>}
            {can("media.scout") && <NavLink to="/media-scout">{t("nav.mediaScout")}</NavLink>}
            {can("strategy.view") && <NavLink to="/strategy">{t("nav.strategy")}</NavLink>}
            {can("aura.query") && <NavLink to="/advisor">{t("nav.advisor")}</NavLink>}
            {can("aura.query") && <NavLink to="/memory">{t("nav.memory")}</NavLink>}
            {(can("evidence.ingest") || can("aura.query")) && (
              <NavLink to="/evidence">{t("nav.evidence")}</NavLink>
            )}
            {/* The candidate holds these permissions by design, but neither an
                evidence-QA queue nor an engineering tool catalogue belongs on
                the phone he checks between doors. The routes stay reachable. */}
            {can("aura.canonical.approve") && !candidateHome && (
              <NavLink to="/claims-review">{t("nav.claimsReview")}</NavLink>
            )}
            {can("aura.query") && !candidateHome && (
              <NavLink to="/war-room">{t("nav.warRoom")}</NavLink>
            )}
            {can("aura.query") && <NavLink to="/watchdog">{t("nav.watchdog")}</NavLink>}
            {can("field.manage") && <NavLink to="/field">{t("nav.field")}</NavLink>}
            {can("crm.view") && <NavLink to="/contacts">{t("nav.contacts")}</NavLink>}
            {can("projects.view") && <NavLink to="/work">{t("nav.work")}</NavLink>}
            {can("projects.view") && <NavLink to="/projects">{t("nav.projects")}</NavLink>}
            {can("projects.view") && <NavLink to="/issues">{t("nav.issues")}</NavLink>}
            {can("projects.view") && <NavLink to="/decisions">{t("nav.decisions")}</NavLink>}
            {can("projects.view") && <NavLink to="/risks">{t("nav.risks")}</NavLink>}
            {can("projects.view") && <NavLink to="/calendar">{t("nav.calendar")}</NavLink>}
            {can("projects.view") && <NavLink to="/board">{t("nav.board")}</NavLink>}
            {can("projects.view") && <NavLink to="/report">{t("nav.report")}</NavLink>}
            {can("hermes.run") && <NavLink to="/hermes">{t("nav.hermes")}</NavLink>}
            {can("finance.view") && <NavLink to="/finance">{t("nav.finance")}</NavLink>}
            {can("finance.view") && <NavLink to="/vendors">{t("nav.vendors")}</NavLink>}
            {can("finance.view") && <NavLink to="/filings">{t("nav.filings")}</NavLink>}
            {(can("admin.users.manage") || can("audit.view")) && (
              <NavLink to="/admin">{t("nav.admin")}</NavLink>
            )}
            {permissions.includes("system.maintenance") && (
              <NavLink to="/console">{t("nav.console")}</NavLink>
            )}
            <NavLink to="/settings">{t("nav.settings")}</NavLink>
            <NavLink to="/guide">{t("nav.guide")}</NavLink>
            {(trialActive ||
              can("finance.view") ||
              can("briefing.view") ||
              profile?.roles?.includes("super_admin") ||
              profile?.roles?.includes("developer")) && (
              <NavLink to="/billing">{t("nav.billing")}</NavLink>
            )}
          </>
        )}
      </nav>

      <div className="sidebar-foot">
        <div>
          <div className="who-name">{profile?.displayName}</div>
          <div className="who-role">{profile?.title ?? profile?.roles?.join(", ") ?? ""}</div>
        </div>

        {canPreview && (
          <label className="view-as">
            <span className="faint small">{locale === "es" ? "Ver como" : "View as"}</span>
            <select
              value={viewAsRole ?? ""}
              onChange={(e) => setViewAsRole(e.target.value || null)}
              aria-label={locale === "es" ? "Ver como rol" : "View as role"}
            >
              <option value="">{locale === "es" ? "Mi cuenta (todo)" : "My account (full)"}</option>
              {PREVIEWABLE_ROLES.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="lang-toggle" role="group" aria-label={t("nav.language")}>
          <button type="button" aria-pressed={locale === "es"} onClick={() => setLocale("es")}>
            ES
          </button>
          <button type="button" aria-pressed={locale === "en"} onClick={() => setLocale("en")}>
            EN
          </button>
        </div>

        <button type="button" className="subtle" onClick={() => void logout()}>
          {t("nav.signOut")}
        </button>
        {/* "*" is unbounded, so a count would understate it. Both developer
            and super_admin carry the wildcard; developer additionally carries
            the explicit maintenance permission. */}
        <div className="faint small mono">
          {permissions.includes("*") ? t("admin.allPermissions") : `${permissions.length} perms`}
        </div>
      </div>
    </aside>
  );
}

/** Phone-only chrome: 56px bar with the drawer toggle, the brand and search. */
function TopBar({ onMenu, onOpenSearch }: { onMenu: () => void; onOpenSearch: () => void }) {
  const { t } = useSession();
  return (
    <div className="topbar">
      <button type="button" className="topbar-btn" onClick={onMenu} aria-label={t("nav.menu")}>
        ☰
      </button>
      <span className="topbar-brand">{t("app.name")}</span>
      <button
        type="button"
        className="topbar-btn"
        onClick={onOpenSearch}
        aria-label={t("search.open")}
      >
        🔍
      </button>
    </div>
  );
}

interface TabItem {
  to: string;
  labelKey: StringKey;
  icon: string;
}

/**
 * Phone-only bottom bar. Five destinations, chosen by what the viewer does:
 * the candidate gets his own verbs, everyone else gets the day's work. The
 * fifth slot always opens the full drawer, so nothing becomes unreachable.
 */
function BottomTabs({ onMenu }: { onMenu: () => void }) {
  const { t, can } = useSession();
  const candidateHome = useCandidateHome();

  // Short labels: a tab bar has about 70px per item, and the sidebar names
  // ("My campaign today", "Debate practice") wrap to three lines in it.
  const candidateTabs: TabItem[] = [
    { to: "/today", labelKey: "tab.today", icon: "☀" },
    { to: "/call-time", labelKey: "tab.calls", icon: "☎" },
    { to: "/practice", labelKey: "tab.practice", icon: "🎤" },
    { to: "/media-scout", labelKey: "tab.media", icon: "📰" },
  ];
  const staffTabs: TabItem[] = [
    {
      to: can("briefing.view") ? "/today" : "/",
      labelKey: can("briefing.view") ? "tab.today" : "tab.overview",
      icon: "☀",
    },
    { to: "/work", labelKey: "tab.tasks", icon: "✓" },
    { to: "/calendar", labelKey: "tab.calendar", icon: "🗓" },
    { to: "/board", labelKey: "tab.chat", icon: "💬" },
  ];
  const permitted = (to: string) =>
    to === "/today"
      ? can("briefing.view")
      : to === "/call-time"
        ? can("calltime.use")
        : to === "/practice"
          ? can("rapidresponse.practice")
          : to === "/media-scout"
            ? can("media.scout")
            : to === "/work" || to === "/calendar" || to === "/board"
              ? can("projects.view")
              : true;

  const tabs = (candidateHome ? candidateTabs : staffTabs).filter((tab) => permitted(tab.to));

  return (
    <nav className="tabbar" aria-label={t("nav.primary")}>
      {tabs.map((tab) => (
        <NavLink key={tab.to} to={tab.to} end={tab.to === "/"}>
          <span className="tabbar-icon" aria-hidden="true">
            {tab.icon}
          </span>
          {t(tab.labelKey)}
        </NavLink>
      ))}
      <button type="button" onClick={onMenu}>
        <span className="tabbar-icon" aria-hidden="true">
          ☰
        </span>
        {t("nav.more")}
      </button>
    </nav>
  );
}

/** Keeps <html lang> in sync so screen readers announce the right language. */
function useDocumentLanguage() {
  const { locale } = useSession();
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
}

export default function App() {
  const {
    firebaseUser,
    loading,
    missingProfile,
    campaignMismatch,
    profile,
    t,
    licenseResolved,
    licensed,
    trial,
    trialResolved,
    trialActive,
    developerBypass,
    demoMode,
  } = useSession();
  const location = useLocation();
  useDocumentLanguage();
  const [searchOpen, setSearchOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  // A route change always closes the phone drawer.
  useEffect(() => setNavOpen(false), [location.pathname]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (loading) {
    return (
      <div className="auth-screen">
        <div className="muted">
          <span className="spinner" /> {t("common.loading")}
        </div>
      </div>
    );
  }

  if (!firebaseUser) {
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Signed in to Firebase Auth but with no user record, or a suspended one.
  // Both are dead ends that need an administrator, so say so plainly instead
  // of dropping the person into an app where every query fails.
  if (missingProfile || !profile) {
    return <Login blockedReason="noAccount" />;
  }
  if (campaignMismatch) {
    return <Login blockedReason="wrongCampaign" />;
  }
  if (profile.status !== "active") {
    return <Login blockedReason="suspended" />;
  }
  if (!licenseResolved || !trialResolved) {
    return (
      <div className="auth-screen">
        <div className="muted">
          <span className="spinner" /> {t("common.loading")}
        </div>
      </div>
    );
  }
  // A provisioned account's temporary password is replaced before anything else.
  if (profile.mustChangePassword === true && !demoMode) {
    return <PasswordChange />;
  }
  // No license, trial or bypass: keep the shell and sidebar and show Billing
  // inside it, rather than a bare full-screen paywall.
  if (!licensed && !trialActive && !developerBypass && !demoMode) {
    return (
      <div className="shell">
        <TopBar onMenu={() => setNavOpen(true)} onOpenSearch={() => undefined} />
        <Sidebar
          onOpenSearch={() => undefined}
          locked
          open={navOpen}
          onClose={() => setNavOpen(false)}
        />
        {navOpen && (
          <div className="nav-scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />
        )}
        <main className="main">
          <Suspense
            fallback={
              <div className="muted">
                <span className="spinner" /> {t("common.loading")}
              </div>
            }
          >
            <Routes>
              <Route path="/guide" element={<Guide />} />
              <Route path="*" element={<Billing />} />
            </Routes>
          </Suspense>
          <SiteFooter />
        </main>
      </div>
    );
  }

  return (
    <div className="shell">
      <TopBar onMenu={() => setNavOpen(true)} onOpenSearch={() => setSearchOpen(true)} />
      <Sidebar
        onOpenSearch={() => setSearchOpen(true)}
        open={navOpen}
        onClose={() => setNavOpen(false)}
      />
      {navOpen && (
        <div className="nav-scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />
      )}
      {searchOpen && <Search onClose={() => setSearchOpen(false)} />}
      <main className="main">
        {trialActive && trial && <TrialBanner />}
        <DemoExperience />
        <Onboarding />
        <PreviewBanner />
        <div key={location.pathname}>
          <Suspense
            fallback={
              <div className="muted">
                <span className="spinner" /> {t("common.loading")}
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<Home />} />
              <Route
                path="/today"
                element={
                  <Gate path="/today">
                    <Briefing />
                  </Gate>
                }
              />
              <Route
                path="/command-center"
                element={
                  <Gate path="/command-center">
                    <Dashboard />
                  </Gate>
                }
              />
              <Route
                path="/call-time"
                element={
                  <Gate path="/call-time">
                    <CallTime />
                  </Gate>
                }
              />
              <Route
                path="/practice"
                element={
                  <Gate path="/practice">
                    <Practice />
                  </Gate>
                }
              />
              <Route
                path="/media-scout"
                element={
                  <Gate path="/media-scout">
                    <MediaScout />
                  </Gate>
                }
              />
              <Route
                path="/strategy"
                element={
                  <Gate path="/strategy">
                    <Strategy />
                  </Gate>
                }
              />
              <Route
                path="/advisor"
                element={
                  <Gate path="/advisor">
                    <Advisor />
                  </Gate>
                }
              />
              <Route
                path="/memory"
                element={
                  <Gate path="/memory">
                    <Memory />
                  </Gate>
                }
              />
              <Route
                path="/war-room"
                element={
                  <Gate path="/war-room">
                    <WarRoom />
                  </Gate>
                }
              />
              <Route
                path="/watchdog"
                element={
                  <Gate path="/watchdog">
                    <Watchdog />
                  </Gate>
                }
              />
              <Route
                path="/field"
                element={
                  <Gate path="/field">
                    <Field />
                  </Gate>
                }
              />
              <Route
                path="/calendar"
                element={
                  <Gate path="/calendar">
                    <Calendar />
                  </Gate>
                }
              />
              <Route
                path="/board"
                element={
                  <Gate path="/board">
                    <Board />
                  </Gate>
                }
              />
              <Route
                path="/projects"
                element={
                  <Gate path="/projects">
                    <Projects />
                  </Gate>
                }
              />
              <Route
                path="/evidence"
                element={
                  <Gate path="/evidence">
                    <Evidence />
                  </Gate>
                }
              />
              <Route
                path="/evidence/:evidenceId"
                element={
                  <Gate path="/evidence">
                    <EvidenceDetail />
                  </Gate>
                }
              />
              <Route
                path="/claims-review"
                element={
                  <Gate path="/claims-review">
                    <ClaimsReview />
                  </Gate>
                }
              />
              <Route
                path="/contacts"
                element={
                  <Gate path="/contacts">
                    <Contacts />
                  </Gate>
                }
              />
              <Route
                path="/issues"
                element={
                  <Gate path="/issues">
                    <Issues />
                  </Gate>
                }
              />
              <Route
                path="/decisions"
                element={
                  <Gate path="/decisions">
                    <Decisions />
                  </Gate>
                }
              />
              <Route
                path="/risks"
                element={
                  <Gate path="/risks">
                    <Risks />
                  </Gate>
                }
              />
              <Route
                path="/work"
                element={
                  <Gate path="/work">
                    <Work />
                  </Gate>
                }
              />
              <Route
                path="/finance"
                element={
                  <Gate path="/finance">
                    <Finance />
                  </Gate>
                }
              />
              <Route
                path="/vendors"
                element={
                  <Gate path="/vendors">
                    <Vendors />
                  </Gate>
                }
              />
              <Route
                path="/filings"
                element={
                  <Gate path="/filings">
                    <Filings />
                  </Gate>
                }
              />
              <Route
                path="/report"
                element={
                  <Gate path="/report">
                    <Report />
                  </Gate>
                }
              />
              <Route
                path="/hermes"
                element={
                  <Gate path="/hermes">
                    <Hermes />
                  </Gate>
                }
              />
              <Route
                path="/admin"
                element={
                  <Gate path="/admin">
                    <Admin />
                  </Gate>
                }
              />
              <Route path="/settings" element={<Settings />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/guide" element={<Guide />} />
              <Route
                path="/console"
                element={
                  <Gate path="/console">
                    <Console />
                  </Gate>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </div>
        <SiteFooter />
      </main>
      <BottomTabs onMenu={() => setNavOpen(true)} />
      <Assistant />
      <FirstRunWizard />
    </div>
  );
}

/** Sticky banner shown while a privileged user previews another role's view. */
function PreviewBanner() {
  const { viewAsRole, setViewAsRole, locale } = useSession();
  if (!viewAsRole) return null;
  return (
    <div className="preview-banner" role="status">
      <span>
        {locale === "es" ? "Viendo como" : "Viewing as"} <strong>{viewAsRole}</strong>
        {locale === "es"
          ? " — vista previa de solo lectura de lo que ve este rol."
          : " — read-only preview of what this role sees."}
      </span>
      <button type="button" className="subtle" onClick={() => setViewAsRole(null)}>
        {locale === "es" ? "Salir de la vista previa" : "Exit preview"}
      </button>
    </div>
  );
}

/** Opens the setup tour on first login, and whenever Settings requests it. */
function FirstRunWizard() {
  const { demoMode, firebaseUser, profile } = useSession();
  const [open, setOpen] = useState(
    () => !wizardDone(firebaseUser?.uid) && profile?.welcomePending === undefined,
  );
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-wizard", handler);
    return () => window.removeEventListener("open-wizard", handler);
  }, []);
  if (demoMode) return null;
  return open ? <Wizard onClose={() => setOpen(false)} /> : null;
}

function TrialBanner() {
  const { trial, locale } = useSession();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  if (!trial) return null;
  const remainingMs = Math.max(0, trial.endsAt.toMillis() - now);
  const hours = Math.floor(remainingMs / 3_600_000);
  const minutes = Math.floor((remainingMs % 3_600_000) / 60_000);
  const remainingUsd = Math.max(
    0,
    (trial.spendLimitMicros - trial.spentMicros - trial.reservedMicros) / 1_000_000,
  );
  return (
    <div className="preview-banner" role="status">
      <span>
        <strong>{locale === "es" ? "Prueba completa" : "Full-access trial"}</strong>
        {locale === "es"
          ? ` · ${hours}h ${minutes}m restantes · $${remainingUsd.toFixed(2)} de IA disponibles`
          : ` · ${hours}h ${minutes}m remaining · $${remainingUsd.toFixed(2)} AI budget available`}
      </span>
      <NavLink to="/billing">
        {locale === "es" ? "Activar suscripción" : "Activate subscription"}
      </NavLink>
    </div>
  );
}

export function SiteFooter() {
  return (
    <footer className="app-footer">
      Built by{" "}
      <a href="https://elevateelpaso.com" target="_blank" rel="noreferrer noopener">
        Elevateelpaso.com
      </a>{" "}
      · Powered by Passion for Life
    </footer>
  );
}
